import {
  Button,
  Form,
  InputCurrency,
  InputNumber,
} from "@sellar-io/components";
import { ProductListResponseType } from "@sellar-io/api-v2/types";
import { TanStackFormField } from "@sellar-io/components/adapters";
import { useForm } from "@tanstack/react-form";
import { useSelector } from "@tanstack/react-store";
import {
  findProductFulfillmentTypeDeliveryCharge,
  formatCurrency,
} from "@sellar-io/common";
import { ReactNode } from "react";
import { Icon, StockIndicator } from "../../../../components";
import { AvailableFromBadge } from "~/components/badge/AvailableFromBadge";
import { MaxOrderQuantityBadge } from "~/components/badge/MaxOrderQuantityBadge";
import { Switch } from "@sellar-io/components";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
  usePriceFormat,
} from "~/hooks/usePriceFormat";
import {
  OrderOnBehalfOfOrderItemType,
  useOrderOnBehalfOfContext,
} from "../OrderOnBehalfOf.shared";

type OrderItemFormValues = {
  quantity?: number;
  price: string;
  discountable: boolean;
};

export const OrderItemForm = ({
  orderItem,
  onRemoveItem,
  product,
  onSubmit,
  children,
}: {
  orderItem?: OrderOnBehalfOfOrderItemType;
  onRemoveItem?: () => void;
  product: ProductListResponseType[number];
  onSubmit?: (orderItem: OrderOnBehalfOfOrderItemType) => void;
  children?: ReactNode;
}) => {
  const { fulfillmentTypeContainerDeliveryCharges } =
    useOrderOnBehalfOfContext();

  const fullPriceFormat = usePriceFormat(product.ProductPriceTier?.fullPrice);

  const form = useForm({
    defaultValues: {
      quantity: orderItem?.quantity,
      price: formatPriceApiToInput(
        orderItem?.price ?? product.ProductPriceTier?.price ?? null,
      ),
      discountable:
        orderItem?.discountable ??
        product.ProductPriceTier?.discountable ??
        product.type !== "badge",
    } satisfies OrderItemFormValues,
    onSubmit: ({ value }) => {
      if (value.quantity === undefined) throw new Error("Quantity not defined");
      const price = formatPriceInputToApi(value.price);

      if (price === null) throw new Error("Price not defined");
      if (price < 0) throw new Error("Price less than 0");

      const fullPrice = fullPriceFormat.apiFormat ?? 0;

      onSubmit?.({
        Product: product,
        quantity: value.quantity,
        price,
        discountable: value.discountable,
        fullPrice,
      });
    },
  });

  const { quantity, price } = useSelector(form.store, (state) => ({
    quantity: state.values.quantity,
    price: formatPriceInputToApi(state.values.price),
  }));

  const deliveryCharge =
    findProductFulfillmentTypeDeliveryCharge({
      fulfillmentTypeContainerDeliveryCharges:
        fulfillmentTypeContainerDeliveryCharges,
      product: {
        ...product,
        displayUnit: product.unit,
      },
    })?.charge ?? 0;

  const canBackOrder = product.canBackorder && product.availableStock < 1;

  return (
    <Form className={"flex flex-col gap-4"} onSubmit={form.handleSubmit}>
      <div
        className={"divide-y overflow-hidden rounded-lg border border-solid"}
      >
        <div className="flex flex-col gap-2 p-4">
          <div className={"flex items-center justify-between"}>
            <div className="flex flex-col gap-1">
              <div className={"flex items-center gap-2"}>
                <StockIndicator
                  stock={product.availableStock}
                  canBackorder={canBackOrder}
                  type="short"
                />
                {product.name}
              </div>
              {canBackOrder && (
                <span className="text-sm text-zinc-500">
                  Continuous back ordering is enabled for this product.
                </span>
              )}
            </div>

            {onRemoveItem && (
              <Button onClick={onRemoveItem} color={"secondary"} size={"small"}>
                <Icon icon={"CLOSE"} />
              </Button>
            )}
          </div>

          {!!product.availableFrom && (
            <AvailableFromBadge availableFrom={product.availableFrom} />
          )}
        </div>

        <div className={"flex items-center justify-between p-4"}>
          <div className="flex flex-col gap-2">
            <div>Quantity</div>
            {!!product.maxOrderQuantity && (
              <MaxOrderQuantityBadge maxQuantity={product.maxOrderQuantity} />
            )}
          </div>

          <TanStackFormField
            form={form}
            name="quantity"
            label="Quantity"
            className="w-full max-w-[456px]"
            labelClassName="sr-only"
            validators={{
              onSubmit: ({ value }) =>
                value === undefined || value < 1
                  ? "Quantity must be at least 1."
                  : undefined,
            }}
          >
            {({ numberInputProps }) => (
              <InputNumber
                {...numberInputProps}
                step="1"
                fractionDigits={0}
                autoFocus
              />
            )}
          </TanStackFormField>
        </div>
        <div className={"flex items-center justify-between p-4"}>
          <span>Price (per unit)</span>
          <TanStackFormField
            form={form}
            name="price"
            label="Price (per unit)"
            className="w-full max-w-[456px]"
            labelClassName="sr-only"
            validators={{
              onSubmit: ({ value }) => {
                const price = formatPriceInputToApi(value);

                if (price === null) return "Price is required.";
                if (price < 0) return "Price must be at least £0.";
                return undefined;
              },
            }}
          >
            {({ inputProps, setValue }) => (
              <InputCurrency
                {...inputProps}
                showPrefix
                step="any"
                onChange={(event) => setValue(event.target.value)}
              />
            )}
          </TanStackFormField>
        </div>
        <div className={"flex items-center justify-between p-4"}>
          <span>Discountable</span>
          <form.Field name="discountable">
            {({ state, handleChange }) => (
              <Switch
                checked={state.value}
                onChange={() => {
                  handleChange(!state.value);
                }}
              />
            )}
          </form.Field>
        </div>
      </div>
      <div className={"flex flex-col gap-2"}>
        <div className={"flex justify-between px-4"}>
          <span>Line price</span>
          <span>
            {formatCurrency(quantity && price ? price * quantity : 0)}
          </span>
        </div>
        <div className={"flex justify-between px-4"}>
          <span>Item delivery charge</span>
          <div>
            <span>{formatCurrency(quantity ? deliveryCharge : 0)} </span>
            <span>
              {!!quantity &&
                quantity > 1 &&
                `(${formatCurrency(deliveryCharge * quantity)})`}
            </span>
          </div>
        </div>
      </div>

      {children}
    </Form>
  );
};
